import { apiClient } from './axios';
import { config } from '@/lib/config';
import { userAuthorizationResponseSchema, type UserAuthorizationResponse } from '@/lib/schemas';

/**
 * Parse the auth response, but if the server returns something we don't
 * recognise we still try to return whatever's there. The login page only
 * needs `reason` and `oauth_token.access_token`; throwing on a
 * field-shape mismatch (e.g. clinician.id being a string) would give the
 * user a "Sign in failed" toast on what is actually a successful auth.
 */
const safeParseAuth = (raw: unknown): UserAuthorizationResponse => {
  const parsed = userAuthorizationResponseSchema.safeParse(raw);
  if (parsed.success) return parsed.data;
  if (import.meta.env.DEV) {
    console.warn('Auth response failed schema validation; passing through raw shape:', parsed.error.issues);
  }
  return (raw ?? {}) as UserAuthorizationResponse;
};

/**
 * Build a `FormData` request body matching v1's `services/auth.ts:14-27`
 * exactly — including unconditionally appending fields that may be undefined.
 *
 * v1 has been exchanging OAuth credentials this way since launch and we
 * deliberately mirror it byte-for-byte (multipart/form-data + the same field
 * order) to remove any "is the server rejecting our shape?" variable. axios
 * sets the multipart Content-Type with the boundary automatically when the
 * body is a FormData instance.
 */
const buildAuthorizeFormData = (params: Record<string, string | undefined>): FormData => {
  const fd = new FormData();
  for (const [k, v] of Object.entries(params)) {
    fd.append(k, v ?? '');
  }
  return fd;
};

export const authApi = {
  /**
   * Single endpoint for both credential-only login and credential+OTP login.
   * On the first call (no `otp`), the server may respond with a 401 +
   * `reason: "mfa_token_required"` body — callers must handle that as the
   * MFA gate and re-call with the OTP.
   *
   * NOTE: `redirect_uri` is `/` to match v1 (clinician/src/services/auth.ts:25).
   * An earlier V2 comment claimed `/ui/` matched v1; that was wrong, and
   * staging/prod's OAuth allowlist rejects values it doesn't recognise — the
   * MFA-step request would 401 on every submit even when credentials and OTP
   * were correct.
   */
  async authorize(args: {
    username: string;
    password: string;
    otp?: string;
  }): Promise<UserAuthorizationResponse> {
    const body = buildAuthorizeFormData({
      username: args.username,
      password: args.password,
      token: args.otp,
      client_id: config.OAUTH_CLIENT_ID,
      redirect_uri: '/',
      response_type: 'token',
    });
    const res = await apiClient.post('/oauth/authorize', body);
    return safeParseAuth(res.data);
  },

  async authorizeMagicLink(secureToken: string): Promise<UserAuthorizationResponse> {
    const body = buildAuthorizeFormData({
      secure_token: secureToken,
      client_id: config.OAUTH_CLIENT_ID,
      redirect_uri: '/',
      response_type: 'token',
    });
    const res = await apiClient.post('/oauth/authorize_magic_link', body);
    return safeParseAuth(res.data);
  },

  async logout(token: string): Promise<void> {
    // Revocation must use the session being ended, even after a new login.
    await apiClient.post('/oauth/logout', {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },

  async requestPasswordReset(email: string): Promise<void> {
    // Prefer JSON body so the address never appears on the URL (proxy logs,
    // Sentry xhr breadcrumbs). Query `email` is deprecated on the API
    // (FOOD-1323) but still accepted for older clients.
    await apiClient.post('/users/request_password_reset_email', { email });
  },

  async resetPasswordByToken(token: string, newPassword: string): Promise<void> {
    await apiClient.post('/users/reset_password_by_token', { token, new_password: newPassword });
  },

  async currentUser(): Promise<unknown> {
    const res = await apiClient.get('/users/current');
    return res.data;
  },
};
